import { Link } from "react-router-dom";
import { Facebook, Linkedin, Instagram, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n";

const socialLinks = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Instagram, href: "#", label: "Instagram" },
];

export default function Footer() {
  const { t } = useLanguage();

  const footerLinks = {
    services: [
      { labelKey: "footer.fullTruckload", href: "#services" },
      { labelKey: "footer.ltlShipping", href: "#services" },
      { labelKey: "footer.expeditedFreight", href: "#services" },
      { labelKey: "footer.regional", href: "#services" },
      { labelKey: "footer.specialized", href: "#services" },
    ],
    company: [
      { labelKey: "footer.aboutUs", href: "#experience" },
      { labelKey: "footer.ourFleet", href: "#experience" },
      { labelKey: "footer.careers", href: "#drivers" },
      { labelKey: "footer.news", href: "#" },
      { labelKey: "nav.contact", href: "#contact" },
    ],
    drivers: [
      { labelKey: "footer.driveWithUs", href: "#drivers" },
      { labelKey: "footer.benefits", href: "#drivers" },
      { labelKey: "footer.requirements", href: "#drivers" },
      { labelKey: "nav.apply", href: "#apply" },
      { labelKey: "footer.driverPortal", href: "#" },
    ],
    legal: [
      { labelKey: "footer.privacy", href: "#" },
      { labelKey: "footer.terms", href: "#" },
      { labelKey: "footer.cookies", href: "#" },
      { labelKey: "footer.accessibility", href: "#" },
    ],
  };

  return (
    <footer className="bg-secondary border-t border-border">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img
                src="/images/image.png"
                alt="Reyes Trux Corp Logo"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-muted-foreground mb-6 max-w-sm leading-relaxed">{t("footer.description")}</p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Services links */}
          <div>
            <h3 className="font-semibold mb-4 text-foreground">{t("footer.services")}</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.labelKey}>
                  <a href={link.href} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    {t(link.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company links */}
          <div>
            <h3 className="font-semibold mb-4 text-foreground">{t("footer.company")}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.labelKey}>
                  <a href={link.href} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    {t(link.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Drivers links */}
          <div>
            <h3 className="font-semibold mb-4 text-foreground">{t("footer.forDrivers")}</h3>
            <ul className="space-y-3">
              {footerLinks.drivers.map((link) => (
                <li key={link.labelKey}>
                  <a href={link.href} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    {t(link.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {footerLinks.legal.map((link) => (
                <a key={link.labelKey} href={link.href} className="hover:text-primary transition-colors">
                  {t(link.labelKey)}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">© 2026 Reyes Trux Corp. {t("footer.rights")}</p>
              <Button
                variant="outline"
                size="icon"
                asChild
                className="hidden md:flex bg-transparent border-primary/50 hover:bg-primary/10"
              >
                <a href="#" aria-label="Back to top">
                  <ArrowUp className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
